import { useState, useMemo } from 'react'
import { MessageCircle, Copy, ExternalLink, Lightbulb, Phone, CheckCircle2, Sparkles, Hash, Globe } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { Helmet } from 'react-helmet-async'
import FAQAccordion from '../components/FAQAccordion'

const WhatsAppLinkGenerator = () => {
  const { language } = useLanguage()
  const [phoneNumber, setPhoneNumber] = useState('')
  const [preMessage, setPreMessage] = useState('')
  const [generatedLink, setGeneratedLink] = useState('')
  const [copied, setCopied] = useState(false)

  const formatPhoneNumber = (input: string): string => {
    // Sadece rakamları al
    let cleaned = input.replace(/\D/g, '')
    
    // Eğer + ile başlıyorsa kaldır
    if (cleaned.startsWith('+')) {
      cleaned = cleaned.substring(1)
    }
    
    // Türkiye için format kontrolü
    // 05xx ile başlıyorsa 90 ekle
    if (cleaned.startsWith('0') && cleaned.length >= 10) {
      cleaned = '90' + cleaned.substring(1)
    }
    // 90 ile başlamıyorsa ve 10 haneli ise 90 ekle
    else if (!cleaned.startsWith('90') && cleaned.length === 10) {
      cleaned = '90' + cleaned
    }
    
    return cleaned
  }

  const generateLink = () => {
    const formatted = formatPhoneNumber(phoneNumber)
    
    if (!formatted || formatted.length < 10) {
      alert(language === 'tr' 
        ? 'Lütfen geçerli bir telefon numarası girin' 
        : language === 'en' 
        ? 'Please enter a valid phone number' 
        : 'Пожалуйста, введите действительный номер телефона')
      return
    }

    let url = `https://wa.me/${formatted}`
    
    if (preMessage.trim()) {
      const encodedMessage = encodeURIComponent(preMessage.trim())
      url += `?text=${encodedMessage}`
    }
    
    setGeneratedLink(url)
  }

  const copyToClipboard = async () => {
    if (!generatedLink) return
    
    try {
      await navigator.clipboard.writeText(generatedLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Kopyalama hatası:', err)
    }
  }

  const testLink = () => {
    if (generatedLink) {
      window.open(generatedLink, '_blank')
    }
  }

  const translations = {
    tr: {
      title: 'Ücretsiz WhatsApp Link Oluşturucu',
      seoTitle: 'Ücretsiz WhatsApp Link Oluşturucu | İstanbul, Türkiye | Thinklyze',
      seoDescription: 'Ücretsiz WhatsApp link oluşturucu aracı ile telefon numaranız için özel mesaj linkleri oluşturun. İstanbul ve Türkiye genelinde kullanılabilir. Hızlı, kolay ve tamamen ücretsiz.',
      seoKeywords: 'whatsapp link oluşturucu, whatsapp link generator, ücretsiz whatsapp link, whatsapp mesaj linki, türkiye whatsapp link, istanbul whatsapp link, whatsapp bağlantı linki',
      description: 'Telefon numaranız için özel WhatsApp mesaj linkleri oluşturun',
      phoneLabel: 'Telefon Numarası',
      phonePlaceholder: '(5xx) xxx xx xx',
      formatTitle: 'Türkiye için format seçenekleri:',
      format1: '05xx xxx xx xx - Başında 0 ile (otomatik 90 eklenecek)',
      format2: '90 5xx xxx xx xx - Ülke kodu ile',
      format3: '+90 5xx xxx xx xx - + işareti kullanmayın (otomatik kaldırılır)',
      tip: 'Sadece rakamları girin, boşluk ve özel karakterler otomatik temizlenecektir.',
      preMessageLabel: 'Önceden Yazılmış Mesaj (İsteğe Bağlı)',
      preMessagePlaceholder: 'Merhabalar size nasıl ulaşabilirim?',
      preMessageHint: 'Bu mesaj WhatsApp açıldığında otomatik olarak yazılacak',
      generateButton: 'WhatsApp Linki Oluştur',
      generatedTitle: 'Oluşturulan WhatsApp Linki',
      copyButton: 'Kopyala',
      testButton: 'Test Et',
      copied: 'Kopyalandı!',
      invalidPhone: 'Lütfen geçerli bir telefon numarası girin',
      faqTitle: 'Sık Sorulan Sorular',
      faqs: [
        {
          question: 'WhatsApp link oluşturucu ücretsiz mi?',
          answer: 'Evet, WhatsApp link oluşturucu aracımız tamamen ücretsizdir. Herhangi bir ücret ödemeden sınırsız sayıda WhatsApp linki oluşturabilirsiniz.'
        },
        {
          question: 'Oluşturduğum WhatsApp linki ne kadar süre geçerli?',
          answer: 'Oluşturduğunuz WhatsApp linkleri süresiz olarak geçerlidir. Telefon numaranız değişmediği sürece link her zaman çalışacaktır.'
        },
        {
          question: 'WhatsApp linki nasıl kullanılır?',
          answer: 'Oluşturduğunuz linki web sitenize, sosyal medya paylaşımlarınıza veya e-posta imzanıza ekleyebilirsiniz. Kullanıcılar linke tıkladığında WhatsApp açılır ve önceden yazdığınız mesaj otomatik olarak görünür.'
        },
        {
          question: 'Türkiye dışındaki telefon numaraları için de çalışır mı?',
          answer: 'Evet, araç tüm ülke kodlarını destekler. Ancak Türkiye telefon numaraları için otomatik format düzenleme özelliği vardır.'
        },
        {
          question: 'Önceden yazılmış mesaj zorunlu mu?',
          answer: 'Hayır, önceden yazılmış mesaj tamamen isteğe bağlıdır. Sadece telefon numarası ile de link oluşturabilirsiniz.'
        }
      ]
    },
    en: {
      title: 'Free WhatsApp Link Generator',
      seoTitle: 'Free WhatsApp Link Generator | Istanbul, Turkey | Thinklyze',
      seoDescription: 'Create custom WhatsApp message links for your phone number with our free WhatsApp link generator tool. Available in Istanbul and throughout Turkey. Fast, easy and completely free.',
      seoKeywords: 'whatsapp link generator, free whatsapp link, whatsapp message link, turkey whatsapp link, istanbul whatsapp link, whatsapp connection link',
      description: 'Create custom WhatsApp message links for your phone number',
      phoneLabel: 'Phone Number',
      phonePlaceholder: '(5xx) xxx xx xx',
      formatTitle: 'Format options for Turkey:',
      format1: '05xx xxx xx xx - Starting with 0 (90 will be added automatically)',
      format2: '90 5xx xxx xx xx - With country code',
      format3: '+90 5xx xxx xx xx - Do not use + sign (automatically removed)',
      tip: 'Only enter digits, spaces and special characters will be automatically cleaned.',
      preMessageLabel: 'Pre-written Message (Optional)',
      preMessagePlaceholder: 'Hello, how can I reach you?',
      preMessageHint: 'This message will be automatically written when WhatsApp opens',
      generateButton: 'Create WhatsApp Link',
      generatedTitle: 'Generated WhatsApp Link',
      copyButton: 'Copy',
      testButton: 'Test',
      copied: 'Copied!',
      invalidPhone: 'Please enter a valid phone number',
      faqTitle: 'Frequently Asked Questions',
      faqs: [
        {
          question: 'Is the WhatsApp link generator free?',
          answer: 'Yes, our WhatsApp link generator tool is completely free. You can create unlimited WhatsApp links without any cost.'
        },
        {
          question: 'How long is my WhatsApp link valid?',
          answer: 'Your WhatsApp links are valid indefinitely. As long as your phone number doesn\'t change, the link will always work.'
        },
        {
          question: 'How do I use a WhatsApp link?',
          answer: 'You can add the generated link to your website, social media posts, or email signature. When users click the link, WhatsApp opens with your pre-written message automatically displayed.'
        },
        {
          question: 'Does it work for phone numbers outside Turkey?',
          answer: 'Yes, the tool supports all country codes. However, it has automatic format adjustment for Turkish phone numbers.'
        },
        {
          question: 'Is a pre-written message required?',
          answer: 'No, the pre-written message is completely optional. You can create a link with just the phone number only.'
        }
      ]
    },
    ru: {
      title: 'Бесплатный генератор ссылок WhatsApp',
      seoTitle: 'Бесплатный генератор ссылок WhatsApp | Стамбул, Турция | Thinklyze',
      seoDescription: 'Создавайте пользовательские ссылки для сообщений WhatsApp с помощью нашего бесплатного инструмента. Доступно в Стамбуле и по всей Турции. Быстро, легко и совершенно бесплатно.',
      seoKeywords: 'генератор ссылок whatsapp, бесплатная ссылка whatsapp, ссылка сообщения whatsapp, ссылка whatsapp турция, ссылка whatsapp стамбул',
      description: 'Создавайте пользовательские ссылки для сообщений WhatsApp для вашего номера телефона',
      phoneLabel: 'Номер телефона',
      phonePlaceholder: '(5xx) xxx xx xx',
      formatTitle: 'Варианты формата для Турции:',
      format1: '05xx xxx xx xx - Начиная с 0 (90 будет добавлено автоматически)',
      format2: '90 5xx xxx xx xx - С кодом страны',
      format3: '+90 5xx xxx xx xx - Не используйте знак + (автоматически удаляется)',
      tip: 'Вводите только цифры, пробелы и специальные символы будут автоматически удалены.',
      preMessageLabel: 'Предварительно написанное сообщение (необязательно)',
      preMessagePlaceholder: 'Здравствуйте, как с вами связаться?',
      preMessageHint: 'Это сообщение будет автоматически написано при открытии WhatsApp',
      generateButton: 'Создать ссылку WhatsApp',
      generatedTitle: 'Созданная ссылка WhatsApp',
      copyButton: 'Копировать',
      testButton: 'Тест',
      copied: 'Скопировано!',
      invalidPhone: 'Пожалуйста, введите действительный номер телефона',
      faqTitle: 'Часто задаваемые вопросы',
      faqs: [
        {
          question: 'Генератор ссылок WhatsApp бесплатный?',
          answer: 'Да, наш инструмент генератора ссылок WhatsApp полностью бесплатный. Вы можете создавать неограниченное количество ссылок WhatsApp без какой-либо платы.'
        },
        {
          question: 'Как долго действительна моя ссылка WhatsApp?',
          answer: 'Ваши ссылки WhatsApp действительны бессрочно. Пока ваш номер телефона не изменится, ссылка всегда будет работать.'
        },
        {
          question: 'Как использовать ссылку WhatsApp?',
          answer: 'Вы можете добавить созданную ссылку на свой веб-сайт, в публикации в социальных сетях или в подпись электронной почты. Когда пользователи нажимают на ссылку, WhatsApp открывается с автоматически отображаемым предварительно написанным сообщением.'
        },
        {
          question: 'Работает ли это для номеров телефонов вне Турции?',
          answer: 'Да, инструмент поддерживает все коды стран. Однако для турецких номеров телефонов есть автоматическая корректировка формата.'
        },
        {
          question: 'Обязательно ли предварительно написанное сообщение?',
          answer: 'Нет, предварительно написанное сообщение полностью необязательно. Вы можете создать ссылку только с номером телефона.'
        }
      ]
    }
  }

  const t = translations[language as keyof typeof translations] || translations.tr

  // SEO Schema Markup
  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: language === 'tr' ? 'Ana Sayfa' : language === 'en' ? 'Home' : 'Главная',
        item: 'https://thinklyze.com/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: language === 'tr' ? 'Hizmetler' : language === 'en' ? 'Services' : 'Услуги',
        item: 'https://thinklyze.com/services'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: t.title,
        item: 'https://thinklyze.com/whatsapp-link-generator'
      }
    ]
  }), [language, t.title])

  const howToSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: t.title,
    description: t.description,
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: language === 'tr' ? 'Telefon numaranızı girin' : language === 'en' ? 'Enter your phone number' : 'Введите номер телефона',
        text: language === 'tr' ? 'Telefon numarası alanına numaranızı girin. Türkiye için 05xx, 90 5xx veya +90 5xx formatlarını kullanabilirsiniz.' : language === 'en' ? 'Enter your phone number in the phone number field. For Turkey, you can use 05xx, 90 5xx or +90 5xx formats.' : 'Введите номер телефона в поле номера телефона. Для Турции вы можете использовать форматы 05xx, 90 5xx или +90 5xx.'
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: language === 'tr' ? 'Opsiyonel mesaj ekleyin' : language === 'en' ? 'Add optional message' : 'Добавьте необязательное сообщение',
        text: language === 'tr' ? 'İsteğe bağlı olarak WhatsApp açıldığında otomatik görünecek bir mesaj ekleyebilirsiniz.' : language === 'en' ? 'Optionally, you can add a message that will automatically appear when WhatsApp opens.' : 'При желании вы можете добавить сообщение, которое автоматически появится при открытии WhatsApp.'
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: language === 'tr' ? 'Linki oluşturun' : language === 'en' ? 'Generate the link' : 'Создайте ссылку',
        text: language === 'tr' ? '\'WhatsApp Linki Oluştur\' butonuna tıklayın ve linkinizi alın.' : language === 'en' ? 'Click the \'Create WhatsApp Link\' button and get your link.' : 'Нажмите кнопку \'Создать ссылку WhatsApp\' и получите свою ссылку.'
      }
    ]
  }), [language, t.title, t.description])

  const faqSchema = useMemo(() => {
    if (!t.faqs || t.faqs.length === 0) return null
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: t.faqs.map((faq: any) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }
  }, [t.faqs])

  const webPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t.seoTitle || t.title,
    description: t.seoDescription || t.description,
    url: 'https://thinklyze.com/whatsapp-link-generator',
    inLanguage: language === 'tr' ? 'tr-TR' : language === 'en' ? 'en-US' : 'ru-RU',
    about: {
      '@type': 'Thing',
      name: 'WhatsApp Link Generator',
      description: t.description
    },
    provider: {
      '@type': 'Organization',
      name: 'Thinklyze',
      url: 'https://thinklyze.com',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Bakırköy',
        addressRegion: 'İstanbul',
        addressCountry: 'TR'
      }
    }
  }), [language, t])

  return (
    <>
      <Helmet>
        <title>{t.seoTitle || t.title} | Thinklyze</title>
        <meta name="description" content={t.seoDescription || t.description} />
        <meta name="keywords" content={t.seoKeywords} />
        <link rel="canonical" href="https://thinklyze.com/whatsapp-link-generator" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={t.seoTitle || t.title} />
        <meta property="og:description" content={t.seoDescription || t.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thinklyze.com/whatsapp-link-generator" />
        <meta property="og:image" content="https://thinklyze.com/images/logo_siyah.webp" />
        <meta property="og:locale" content={language === 'tr' ? 'tr_TR' : language === 'en' ? 'en_US' : 'ru_RU'} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.seoTitle || t.title} />
        <meta name="twitter:description" content={t.seoDescription || t.description} />
        <meta name="twitter:image" content="https://thinklyze.com/images/logo_siyah.webp" />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="TR-34" />
        <meta name="geo.placename" content="İstanbul" />
        <meta name="geo.position" content="41.0082;28.9784" />
        <meta name="ICBM" content="41.0082, 28.9784" />
      </Helmet>
      
      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      
      <div className="pt-16 bg-[#f9f9f9] relative min-h-screen">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 relative z-10">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 mb-6 sm:mb-8">
              <Sparkles className="w-4 h-4 text-[#25D366]" />
              <span className="text-xs sm:text-[13px] font-medium text-gray-600 bg-white px-3 sm:px-4 py-1.5 rounded-full border border-gray-200 shadow-sm">
                {language === 'tr' ? 'Ücretsiz Araç' : language === 'en' ? 'Free Tool' : 'Бесплатный инструмент'}
              </span>
            </div>
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-[#25D366] opacity-20 blur-xl rounded-full"></div>
                <img 
                  src="/images/thinklyze_whatsapp.png" 
                  alt="WhatsApp" 
                  className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 relative z-10 object-contain drop-shadow-md"
                />
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-4 sm:mb-6">
              {t.title}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-2xl mx-auto mb-3 sm:mb-4 leading-relaxed px-4 sm:px-0">
              {t.description}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 max-w-2xl mx-auto px-4 sm:px-0">
              {language === 'tr' 
                ? 'İstanbul, Türkiye\'de dijital pazarlama profesyonelleri için ücretsiz araç' 
                : language === 'en' 
                ? 'Free tool for digital marketing professionals in Istanbul, Turkey' 
                : 'Бесплатный инструмент для специалистов по цифровому маркетингу в Стамбуле, Турция'}
            </p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-5 sm:p-6 md:p-8 lg:p-10 mb-6 sm:mb-8 border border-gray-100 hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-300">
            {/* Phone Number Input */}
            <div className="mb-6 sm:mb-8">
              <label htmlFor="phone" className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                <Phone className="w-4 h-4 text-[#25D366] flex-shrink-0" />
                {t.phoneLabel} *
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="phone"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={t.phonePlaceholder}
                  className="w-full px-4 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#25D366] focus:border-[#25D366] transition-all duration-200 text-base sm:text-lg bg-gray-50 focus:bg-white"
                />
                {phoneNumber && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <CheckCircle2 className="w-5 h-5 text-[#25D366]" />
                  </div>
                )}
              </div>
            </div>

            {/* Format Options */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center gap-2 mb-4 sm:mb-5">
                <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-[#25D366] flex-shrink-0" />
                <h3 className="text-sm sm:text-base font-bold text-gray-900">{t.formatTitle}</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div className="bg-white border-2 border-gray-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:border-[#25D366] hover:shadow-lg transition-all duration-300 group cursor-pointer">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#25D366] to-[#20BA5A] flex items-center justify-center text-white font-bold text-base sm:text-lg group-hover:scale-110 transition-transform shadow-md flex-shrink-0">
                      1
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs sm:text-sm font-bold text-gray-900 mb-1">05xx xxx xx xx</div>
                      <p className="text-xs text-gray-600 leading-snug">{t.format1}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:border-[#25D366] hover:shadow-lg transition-all duration-300 group cursor-pointer">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#25D366] to-[#20BA5A] flex items-center justify-center text-white font-bold text-base sm:text-lg group-hover:scale-110 transition-transform shadow-md flex-shrink-0">
                      2
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs sm:text-sm font-bold text-gray-900 mb-1">90 5xx xxx xx xx</div>
                      <p className="text-xs text-gray-600 leading-snug">{t.format2}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:border-[#25D366] hover:shadow-lg transition-all duration-300 group cursor-pointer sm:col-span-2 lg:col-span-1">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#25D366] to-[#20BA5A] flex items-center justify-center text-white font-bold text-base sm:text-lg group-hover:scale-110 transition-transform shadow-md flex-shrink-0">
                      3
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs sm:text-sm font-bold text-gray-900 mb-1">+90 5xx xxx xx xx</div>
                      <p className="text-xs text-gray-600 leading-snug">{t.format3}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tip */}
            <div className="relative bg-gradient-to-br from-[#25D366]/5 via-white to-emerald-50/30 border-2 border-[#25D366]/30 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 mb-6 sm:mb-8 overflow-hidden group hover:border-[#25D366]/50 transition-all duration-300">
              {/* Decorative background pattern */}
              <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-[#25D366] opacity-5 rounded-full blur-2xl -mr-12 sm:-mr-16 -mt-12 sm:-mt-16"></div>
              <div className="relative flex items-start gap-3 sm:gap-4 md:gap-5">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[#25D366] to-[#20BA5A] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    <Hash className="w-3 h-3 sm:w-4 sm:h-4 text-[#25D366] flex-shrink-0" />
                    <span className="text-xs font-semibold text-[#25D366] uppercase tracking-wide">
                      {language === 'tr' ? 'İpucu' : language === 'en' ? 'Tip' : 'Совет'}
                    </span>
                  </div>
                  <p className="text-sm sm:text-base text-gray-800 leading-relaxed font-medium">{t.tip}</p>
                </div>
              </div>
            </div>

            {/* Pre-written Message */}
            <div className="mb-6 sm:mb-8">
              <label htmlFor="message" className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                <MessageCircle className="w-4 h-4 text-[#25D366] flex-shrink-0" />
                {t.preMessageLabel}
              </label>
              <textarea
                id="message"
                value={preMessage}
                onChange={(e) => setPreMessage(e.target.value)}
                placeholder={t.preMessagePlaceholder}
                rows={3}
                className="w-full px-4 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#25D366] focus:border-[#25D366] transition-all duration-200 resize-none bg-gray-50 focus:bg-white text-sm sm:text-base"
              />
              <p className="text-xs text-gray-500 mt-2 sm:mt-3 flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-[#25D366] flex-shrink-0"></span>
                {t.preMessageHint}
              </p>
            </div>

            {/* Generate Button */}
            <button
              onClick={generateLink}
              className="w-full bg-gradient-to-r from-[#25D366] to-[#20BA5A] text-white font-semibold py-3 sm:py-4 px-6 rounded-lg sm:rounded-xl hover:from-[#20BA5A] hover:to-[#1DA851] transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 group text-sm sm:text-base"
            >
              <img 
                src="/images/thinklyze_whatsapp.png" 
                alt="WhatsApp" 
                className="w-5 h-5 sm:w-6 sm:h-6 object-contain group-hover:scale-110 transition-transform filter brightness-0 invert"
              />
              <span>{t.generateButton}</span>
            </button>
          </div>

          {/* Generated Link Section */}
          {generatedLink && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-5 sm:p-6 md:p-8 lg:p-10 border border-gray-100 animate-[fadeIn_0.5s_ease-out] mb-6 sm:mb-8">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#25D366] to-[#20BA5A] flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t.generatedTitle}</h2>
              </div>
              <div className="bg-gradient-to-r from-gray-50 to-emerald-50/30 border-2 border-gray-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 mb-4 sm:mb-6 group hover:border-[#25D366] transition-all duration-200">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-2 h-2 rounded-full bg-[#25D366] animate-pulse"></div>
                  </div>
                  <code className="text-xs sm:text-sm text-gray-800 break-all font-mono flex-1">{generatedLink}</code>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  onClick={copyToClipboard}
                  className={`flex-1 font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base ${
                    copied 
                      ? 'bg-gradient-to-r from-[#25D366] to-[#20BA5A] text-white' 
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
                  }`}
                >
                  <Copy className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${copied ? 'animate-bounce' : ''}`} />
                  {copied ? t.copied : t.copyButton}
                </button>
                <button
                  onClick={testLink}
                  className="flex-1 bg-white border-2 border-[#25D366] text-[#25D366] font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl hover:bg-[#25D366] hover:text-white transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-sm sm:text-base"
                >
                  <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  {t.testButton}
                </button>
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {t.faqs && t.faqs.length > 0 && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-5 sm:p-6 md:p-8 lg:p-10 mt-6 sm:mt-8 border border-gray-100">
              <div className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#25D366] to-[#20BA5A] flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t.faqTitle}</h2>
              </div>
              <FAQAccordion 
                faqs={t.faqs.map((faq: any) => ({
                  question: faq.question,
                  answer: faq.answer
                }))}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default WhatsAppLinkGenerator
